import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCampusContext } from "@/contexts/CampusContext";
import { getErrorMessage } from "@/lib/errorHandler";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import API from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  Plus,
  MoreVertical,
  Trash2,
  Search,
  UserPlus,
  Shield,
  User,
  Crown,
  Edit2,
} from "lucide-react";

export default function OrganizationMembers() {
  const { organization, user } = useAuth();
  const { selectedCampus, isOrgAdmin } = useCampusContext();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Dialogs
  const [inviteDialog, setInviteDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Permission labels
  const PERMISSION_LABELS = {
    can_create_quizzes: "Create Quizzes",
    can_edit_quizzes: "Edit Quizzes",
    can_delete_quizzes: "Delete Quizzes",
    can_view_reports: "View Reports",
    can_view_quiz_takers: "View Quiz Takers",
    can_manage_quiz_takers: "Manage Quiz Takers",
    can_export_reports: "Export Reports",
    can_manage_categories: "Manage Categories",
    can_manage_groups: "Manage Groups",
    can_view_system_stats: "View System Stats",
    can_unlock_accounts: "Unlock Accounts",
    can_view_analytics: "View Analytics",
  };

  // Default permissions for new members
  const DEFAULT_PERMISSIONS = {
    can_create_quizzes: true,
    can_edit_quizzes: true,
    can_delete_quizzes: false,
    can_view_reports: true,
    can_view_quiz_takers: true,
    can_manage_quiz_takers: false,
    can_export_reports: false,
    can_manage_categories: true,
    can_manage_groups: true,
    can_view_system_stats: false,
    can_unlock_accounts: false,
    can_view_analytics: false,
  };

  // Forms
  const [inviteData, setInviteData] = useState({
    email: "",
    name: "",
    permissions: { ...DEFAULT_PERMISSIONS },
  });

  const [editData, setEditData] = useState({
    name: "",
    permissions: {},
  });

  const [selectedMember, setSelectedMember] = useState(null);

  // Load members
  const loadMembers = async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 }; // Get all members for now
      if (isOrgAdmin && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }
      const res = await API.get("/organization/members", { params });
      setMembers(res.data.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load members"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization) {
      loadMembers();
    }
  }, [organization, selectedCampus?.id]);

  // Invite member
  const handleInvite = async () => {
    if (!inviteData.email || !inviteData.name) {
      toast.error("Email and name are required");
      return;
    }

    try {
      await API.post("/organization/members/invite", {
        email: inviteData.email,
        name: inviteData.name,
        permissions: inviteData.permissions,
      });

      toast.success("Member invited successfully");
      setInviteDialog(false);
      setInviteData({
        email: "",
        name: "",
        permissions: { ...DEFAULT_PERMISSIONS }
      });
      loadMembers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to invite member"));
    }
  };

  // Edit member
  const handleEdit = async () => {
    if (!selectedMember) return;

    try {
      await API.put(`/organization/members/${selectedMember.id}`, {
        name: editData.name,
        permissions: editData.permissions,
      });

      toast.success("Member updated successfully");
      setEditDialog(false);
      setSelectedMember(null);
      setEditData({ name: "", permissions: {} });
      loadMembers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update member"));
    }
  };

  // Open edit dialog
  const openEditDialog = (member) => {
    setSelectedMember(member);
    setEditData({
      name: member.name,
      permissions: member.permissions || {},
    });
    setEditDialog(true);
  };

  // Delete member
  const handleDelete = async () => {
    try {
      await API.delete(`/user-management/users/${selectedMember.id}`);
      toast.success("Member removed successfully");
      setDeleteDialog(false);
      setSelectedMember(null);
      loadMembers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to remove member"));
    }
  };

  // Filter members
  const filteredMembers = members.filter((member) => {
    const matchesSearch =
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole =
      roleFilter === "all" ||
      (roleFilter === "admin" && member.role === "admin") ||
      (roleFilter === "org_admin" && member.role === "org_admin");

    return matchesSearch && matchesRole;
  });

  // Get role badge variant
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case "superadmin":
        return "default";
      case "admin":
        return "secondary";
      case "user":
        return "outline";
      default:
        return "outline";
    }
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case "superadmin":
        return <Crown className="w-3 h-3" />;
      case "admin":
        return <Shield className="w-3 h-3" />;
      case "user":
        return <User className="w-3 h-3" />;
      default:
        return <User className="w-3 h-3" />;
    }
  };

  if (!organization) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">
            No Organization
          </h2>
          <p className="text-slate-500">
            You are not assigned to an organization yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organization Members</h1>
            <p className="text-slate-500 mt-1">
              Manage members of <strong>{organization.name}</strong>
            </p>
          </div>
          <Button onClick={() => setInviteDialog(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
              <SelectItem value="org_admin">Org Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Members Table */}
        <Card>
          <CardHeader>
            <CardTitle>Members ({filteredMembers.length})</CardTitle>
            <CardDescription>
              All members of your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <>
                    <style>{`@keyframes om-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
                    {[...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><div style={{height:13,width:110,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'om-shimmer 1.4s ease infinite'}} /></TableCell>
                        <TableCell><div style={{height:13,width:150,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'om-shimmer 1.4s ease infinite'}} /></TableCell>
                        <TableCell><div style={{height:20,width:60,borderRadius:10,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'om-shimmer 1.4s ease infinite'}} /></TableCell>
                        <TableCell><div style={{height:13,width:80,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'om-shimmer 1.4s ease infinite'}} /></TableCell>
                        <TableCell><div style={{height:13,width:80,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'om-shimmer 1.4s ease infinite'}} /></TableCell>
                        <TableCell className="text-right"><div style={{height:28,width:50,borderRadius:8,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'om-shimmer 1.4s ease infinite',marginLeft:'auto'}} /></TableCell>
                      </TableRow>
                    ))}
                  </>
                </TableBody>
              </Table>
            ) : filteredMembers.length === 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} className="py-16 text-center">
                      <svg width="72" height="64" viewBox="0 0 72 64" fill="none" className="mx-auto mb-4 opacity-70">
                        <circle cx="28" cy="22" r="10" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
                        <circle cx="28" cy="22" r="5" fill="#A5B4FC"/>
                        <circle cx="46" cy="22" r="10" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
                        <circle cx="46" cy="22" r="5" fill="#A5B4FC"/>
                        <path d="M10 54c2-9 9-14 18-14s16 5 18 14" stroke="#C7D2FE" strokeWidth="2" strokeLinecap="round" fill="none"/>
                        <path d="M36 54c2-9 9-14 18-14" stroke="#C7D2FE" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5"/>
                      </svg>
                      <p className="text-slate-600 font-semibold text-sm">
                        {searchTerm || roleFilter !== "all" ? "No members found" : "No members yet"}
                      </p>
                      <p className="text-slate-400 text-xs mt-1">Invite members to join this organization</p>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold">
                            {member.name?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                          {member.name}
                          {member.id === user.id && (
                            <Badge variant="outline" className="text-xs">
                              You
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(member.role)}>
                          {getRoleIcon(member.role)}
                          <span className="ml-1">{member.role}</span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {member.created_at
                          ? new Date(member.created_at).toLocaleDateString()
                          : "N/A"}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {member.last_login_at
                          ? new Date(member.last_login_at).toLocaleDateString()
                          : "Never"}
                      </TableCell>
                      <TableCell className="text-right">
                        {member.id !== user.id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => openEditDialog(member)}
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit Member
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedMember(member);
                                  setDeleteDialog(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove Member
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Invite Member Dialog */}
        <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Member</DialogTitle>
              <DialogDescription>
                Invite a new member to <strong>{organization.name}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="invite_email">Email *</Label>
                <Input
                  id="invite_email"
                  type="email"
                  value={inviteData.email}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, email: e.target.value })
                  }
                  placeholder="member@example.com"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="invite_name">Name *</Label>
                <Input
                  id="invite_name"
                  value={inviteData.name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, name: e.target.value })
                  }
                  placeholder="John Doe"
                />
              </div>

              {/* Permissions */}
              <div className="grid gap-2">
                <Label className="text-sm font-medium">Permissions</Label>
                <p className="text-xs text-slate-500 mb-2">
                  You can only grant permissions that you have
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto border rounded-md p-3">
                  {Object.entries(PERMISSION_LABELS).map(([key, label]) => {
                    const currentUserHasPermission = user?.permissions?.[key] === true;
                    if (!currentUserHasPermission) return null; // Hide permissions user doesn't have

                    return (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox
                          id={`invite-${key}`}
                          checked={inviteData.permissions[key] || false}
                          onCheckedChange={(checked) =>
                            setInviteData({
                              ...inviteData,
                              permissions: {
                                ...inviteData.permissions,
                                [key]: checked,
                              },
                            })
                          }
                        />
                        <label
                          htmlFor={`invite-${key}`}
                          className="text-sm cursor-pointer"
                        >
                          {label}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setInviteDialog(false);
                  setInviteData({
                    email: "",
                    name: "",
                    permissions: { ...DEFAULT_PERMISSIONS }
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleInvite}
                disabled={!inviteData.email || !inviteData.name}
              >
                Send Invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Member Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Member</DialogTitle>
              <DialogDescription>
                Update member details and permissions
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit_name">Name</Label>
                <Input
                  id="edit_name"
                  value={editData.name}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  placeholder="John Doe"
                />
              </div>

              {/* Permissions */}
              <div className="grid gap-2">
                <Label className="text-sm font-medium">Permissions</Label>
                <p className="text-xs text-slate-500 mb-2">
                  You can only modify permissions that you have. You cannot edit your own permissions.
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto border rounded-md p-3">
                  {Object.entries(PERMISSION_LABELS).map(([key, label]) => {
                    const currentUserHasPermission = user?.permissions?.[key] === true;
                    if (!currentUserHasPermission) return null; // Hide permissions user doesn't have

                    return (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-${key}`}
                          checked={editData.permissions[key] || false}
                          onCheckedChange={(checked) =>
                            setEditData({
                              ...editData,
                              permissions: {
                                ...editData.permissions,
                                [key]: checked,
                              },
                            })
                          }
                        />
                        <label
                          htmlFor={`edit-${key}`}
                          className="text-sm cursor-pointer"
                        >
                          {label}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setEditDialog(false);
                  setSelectedMember(null);
                  setEditData({ name: "", permissions: {} });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleEdit}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Member Dialog */}
        <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Member?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove <strong>{selectedMember?.name}</strong>{" "}
                from your organization? This will delete their account and all associated
                data.
                <strong className="text-red-600 block mt-2">
                  This action cannot be undone!
                </strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Remove Member
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  );
}
